/* P8 — My Tasks: workflow approvals pending for the current user */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, RefreshCw, AlertTriangle, Workflow } from 'lucide-react';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const priorityClass = (p) => ({
  high:   'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
  medium: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  low:    'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
}[p] || 'bg-slate-100 text-slate-700');

const statusClass = (s) => ({
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  APPROVED:         'bg-emerald-100 text-emerald-800',
  REJECTED:         'bg-rose-100 text-rose-800',
  POSTED:           'bg-sky-100 text-sky-800',
  CANCELLED:        'bg-slate-100 text-slate-600',
  DRAFT:            'bg-slate-100 text-slate-700',
}[s] || 'bg-slate-100 text-slate-700');

export default function MyTasks() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);     // selected workflow
  const [detail, setDetail] = useState(null);     // full detail (steps + events)
  const [reason, setReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/workflows/my-tasks`, auth);
      setTasks(r.data.tasks || []);
      setRole(r.data.role || '');
    } catch (e) {
      toast.error(t('mytasks.failed_to_load_tasks'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const openDetail = async (wf) => {
    setActive(wf);
    setReason('');
    try {
      const r = await axios.get(`${API}/workflows/${wf.id}`, auth);
      setDetail(r.data);
    } catch (e) {
      toast.error(t('mytasks.failed_to_load_detail'));
    }
  };

  const closeDetail = () => { setActive(null); setDetail(null); setReason(''); };

  const doApprove = async () => {
    if (!active) return;
    setActionBusy(true);
    try {
      const r = await axios.post(`${API}/workflows/${active.id}/approve`,
        { comment: reason || null }, auth);
      toast.success(`✓ تم الاعتماد / Approved — ${r.data.status}`);
      closeDetail();
      fetchTasks();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : (msg.message || 'Approve failed'));
    } finally {
      setActionBusy(false);
    }
  };

  const doReject = async () => {
    if (!active) return;
    if (!reason.trim()) {
      toast.error(t('mytasks.reason_is_required'));
      return;
    }
    setActionBusy(true);
    try {
      await axios.post(`${API}/workflows/${active.id}/reject`,
        { reason: reason.trim() }, auth);
      toast.success(t('mytasks.rejected'));
      closeDetail();
      fetchTasks();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : (msg.message || 'Reject failed'));
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="my-tasks-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-900 via-indigo-800 to-violet-900 text-white p-8 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Workflow className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">{t('mytasks.my_tasks')}</h1>
              <p className="text-indigo-100 mt-1 text-sm">{t('mytasks.pending_approvals_for_your_role')}</p>
            </div>
          </div>
          <button
            onClick={fetchTasks}
            disabled={loading}
            data-testid="refresh-btn"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-50 transition">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />{t('mytasks.refresh')}</button>
        </div>
        <div className="mt-6 flex flex-wrap gap-4 text-sm">
          <div className="bg-white/10 px-4 py-2 rounded-lg" data-testid="total-tasks">
            <span className="text-indigo-200">{t('mytasks.total')}</span> <b>{tasks.length}</b>
          </div>
          <div className="bg-white/10 px-4 py-2 rounded-lg" data-testid="user-role">
            <span className="text-indigo-200">{t('mytasks.role')}</span> <b>{role || '—'}</b>
          </div>
        </div>
      </div>

      {/* Task list */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="p-5 border-b flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold">{t('mytasks.task_list')}</h2>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400">{t('mytasks.loading')}</div>
        ) : tasks.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">{t('mytasks.no_pending_tasks')}</p>
            <p className="text-slate-400 text-sm mt-1">{t('mytasks.all_caught_up')}</p>
          </div>
        ) : (
          <div className="divide-y" data-testid="tasks-list">
            {tasks.map(t => {
              const sd = t.current_step_detail || {};
              return (
                <div key={t.id}
                     data-testid={`task-${t.document_type}-${t.document_ref}`}
                     onClick={() => openDetail(t)}
                     className="p-5 hover:bg-slate-50 cursor-pointer transition flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-indigo-50 text-indigo-700 font-mono">
                      <span className="text-xs uppercase">{t.document_type.slice(0,3)}</span>
                      <span className="text-sm font-bold">{sd.step}/{t.total_steps}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800">{t.document_ref || t.document_id.slice(0,8)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityClass(t.priority)}`}>
                          {t.priority?.toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(t.status)}`}>
                          {t.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1 truncate">
                        {t.document_snapshot?.description || t.rule_description || '—'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        الخطوة {sd.step} — الدور المطلوب / Required role: <b>{sd.role}</b>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-800">
                      {Number(t.amount || 0).toLocaleString()} <span className="text-xs text-slate-400">SAR</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(t.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail / Action modal */}
      {active && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={closeDetail} data-testid="detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b bg-slate-50 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">{active.document_type.toUpperCase()} — {active.document_ref}</h3>
                <p className="text-sm text-slate-500 mt-1">{active.rule_description}</p>
              </div>
              <button onClick={closeDetail} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="p-5 space-y-5">
              {/* Snapshot */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{t('mytasks.amount')}</div>
                  <div className="font-bold text-slate-800 text-lg">{Number(active.amount).toLocaleString()} SAR</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{t('mytasks.priority')}</div>
                  <div><span className={`px-2 py-1 rounded-full text-xs ${priorityClass(active.priority)}`}>{active.priority?.toUpperCase()}</span></div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 col-span-2">
                  <div className="text-xs text-slate-500">{t('mytasks.created_by')}</div>
                  <div className="text-sm text-slate-700">{active.created_by_name} — {new Date(active.created_at).toLocaleString()}</div>
                </div>
              </div>

              {/* Steps */}
              {detail?.steps?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 text-sm">{t('mytasks.approval_steps')}</h4>
                  <div className="space-y-2" data-testid="approval-steps">
                    {detail.steps.map(s => (
                      <div key={s.step} className="flex items-center gap-3 text-sm p-2 rounded border">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                          s.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          s.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                          'bg-slate-200 text-slate-600'}`}>{s.step}</div>
                        <div className="flex-1">
                          <div className="font-medium text-slate-700">{s.role}</div>
                          {s.approver_user_name && (
                            <div className="text-xs text-slate-400">
                              by {s.approver_user_name} • {s.timestamp ? new Date(s.timestamp).toLocaleString() : ''}
                            </div>
                          )}
                          {s.comment && (
                            <div className="text-xs text-slate-500 italic mt-1">"{s.comment}"</div>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          s.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                          s.status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                          'bg-slate-50 text-slate-600'}`}>{s.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Events timeline */}
              {detail?.events?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 text-sm">{t('mytasks.event_log')}</h4>
                  <div className="space-y-1 text-xs text-slate-600 bg-slate-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {detail.events.map((e,i) => (
                      <div key={i} className="flex justify-between gap-3">
                        <span className="font-medium">{e.event}</span>
                        <span className="text-slate-400">by {e.user_name || 'system'} — {new Date(e.timestamp).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment/reason input */}
              {active.status === 'PENDING_APPROVAL' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">{t('mytasks.required_for_reject')}</label>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={2}
                    data-testid="reason-input"
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('mytasks.write_your_note')} />
                </div>
              )}

              {/* Actions */}
              {active.status === 'PENDING_APPROVAL' && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={doApprove}
                    disabled={actionBusy}
                    data-testid="approve-btn"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50 transition">
                    <CheckCircle2 className="w-5 h-5" />{t('mytasks.approve')}</button>
                  <button
                    onClick={doReject}
                    disabled={actionBusy}
                    data-testid="reject-btn"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold disabled:opacity-50 transition">
                    <XCircle className="w-5 h-5" />{t('mytasks.reject')}</button>
                </div>
              )}

              {active.status !== 'PENDING_APPROVAL' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 text-slate-600 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  هذه الوثيقة في حالة {active.status} ولا يمكن اتخاذ إجراءات / This workflow is {active.status} and cannot be acted upon.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
